import type { CookieSerializeOptions } from 'cookie';
import cookie from 'cookie';
// import { cookies } from 'next/headers'
import { subscribe } from 'valtio';
import { proxyMap } from 'valtio/utils';

const isServer = typeof window === 'undefined';
const isUnset = (o: unknown): boolean => typeof o === 'undefined' || o === null;

const isSet = (o: unknown): boolean => !isUnset(o);

export const GLOBAL_ECART_ID_MAX_AGE = 60 * 60 * 24 * 3;

// Shared by every StorageHelper instance; keyed on the raw cookie string so any change re-parses.
let parsedCookieSource: string | undefined;
let parsedCookies: Record<string, string> = {};

const parseCookies = (cookieStr: string): Record<string, string> => {
  if (cookieStr !== parsedCookieSource) {
    parsedCookies = Object.freeze(cookie.parse(cookieStr) || {});
    parsedCookieSource = cookieStr;
  }
  return parsedCookies;
};

export const subscribeMapKey = <K, V>(
  proxyMapObject: Map<K, V>,
  key: K,
  callback: (v: V | undefined) => void,
  notifyInSync?: boolean,
) => {
  let prev: V | undefined;
  return subscribe(
    proxyMapObject,
    () => {
      const nextValue = proxyMapObject.get(key);
      if (!prev || prev !== nextValue) {
        prev = nextValue;
        callback(nextValue);
      }
    },
    notifyInSync,
  );
};

function decodeValue(val: unknown): unknown {
  // Try to parse as json
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch (_) {
      //do nothing
    }
  }

  // Return as is
  return val;
}

function encodeValue(val: unknown): string {
  if (typeof val === 'string') {
    return val;
  }
  return JSON.stringify(val);
}

// function getProp(
//   holder: Record<string, any>,
//   propName: string | false,
// ): unknown {
//   if (!propName || !holder || typeof holder !== 'object') {
//     return holder;
//   }

//   if (propName in holder) {
//     return holder[propName];
//   }

//   const propParts = Array.isArray(propName)
//     ? propName
//     : (propName + '').split('.');

//   let result = holder;
//   while (propParts.length && result) {
//     result = result[propParts.shift()];
//   }

//   return result;
// }

export interface ModuleOptions {
  cookie:
    | {
        options: {
          domain?: string;
          expires?: number | Date;
          maxAge?: number;
          path: string;
          secure?: boolean;
        };
        prefix: string;
      }
    | false;
  ignoreExceptions: boolean;
  localStorage: { prefix: string } | false;
  sessionStorage: { prefix: string } | false;
  // resetOnError: boolean | ((...args: unknown[]) => boolean)
}

export const moduleDefaults: ModuleOptions = {
  ignoreExceptions: false,

  // -- Cookie Store --

  cookie: {
    prefix: '',
    options: {
      path: '/',
    },
  },

  // -- localStorage Store --

  localStorage: {
    prefix: 'Breitling.',
  },

  // -- sessionStorage Store --

  sessionStorage: {
    prefix: 'Breitling.',
  },
};

//Normalize type at module itself
export type StorageOptions = ModuleOptions & {
  initialState: Array<[any, any]> | null;
};

//Improve type of storages: Universal / Cookie / Local / State
export class StorageHelper {
  private static singleton: StorageHelper;
  //TODO
  // public ctx: Context;
  public options?: StorageOptions;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public state: any;

  constructor(
    options: StorageOptions = {
      initialState: [],
      ...moduleDefaults,
    },
  ) {
    // this.ctx = ctx;

    if (StorageHelper.singleton) {
      return StorageHelper.singleton;
    } else {
      StorageHelper.singleton = this;
      StorageHelper.singleton.options = options;
      StorageHelper.singleton._initState();
      return this;
    }
  }

  static init(options: StorageOptions) {
    if (StorageHelper.singleton) {
      return StorageHelper.singleton;
    } else {
      StorageHelper.singleton = new StorageHelper();
      StorageHelper.singleton.options = options;
      StorageHelper.singleton._initState();
      return StorageHelper.singleton;
    }
  }

  // ------------------------------------
  // Universal
  // ------------------------------------

  setUniversal<V>(key: string, value: V): V | void {
    // Unset null, undefined
    if (isUnset(value)) {
      return this.removeUniversal(key);
    }

    // Cookies
    this.setCookie(key, value);

    // Local Storage
    this.setLocalStorage(key, value);

    // Local state
    this.setState(key, value);

    return value;
  }

  getUniversal(key: string): any {
    let value;

    // Local state
    if (isServer) {
      value = this.getState(key);
    }

    // Cookies
    if (isUnset(value)) {
      value = this.getCookie(key);
    }

    // Local Storage
    if (isUnset(value)) {
      value = this.getLocalStorage(key);
    }

    // Local state
    if (isUnset(value)) {
      value = this.getState(key);
    }

    return value;
  }

  syncUniversal(key: string, defaultValue?: unknown): any {
    let value = this.getUniversal(key);

    if (isUnset(value) && isSet(defaultValue)) {
      value = defaultValue;
    }

    if (isSet(value)) {
      this.setUniversal(key, value);
    }

    return value;
  }

  removeUniversal(key: string): void {
    this.removeState(key);
    this.removeLocalStorage(key);
    this.removeCookie(key);
  }

  // ------------------------------------
  // Local state (reactive)
  // ------------------------------------

  _initState(): void {
    this.state = proxyMap(this.options!.initialState);
  }

  setState<V>(key: string, value: V): V {
    this.state.set(key, value);
    return value;
  }

  getState(key: string): any {
    return this.state.get(key);
  }

  watchState(key: unknown, fn: (value: any) => void, notifyInSync?: boolean) {
    subscribeMapKey(this.state, key, fn, notifyInSync);
  }

  removeState(key: string): void {
    this.setState(key, undefined);
  }

  // ------------------------------------
  // Local storage
  // ------------------------------------

  setLocalStorage<V>(key: string, value: V): V | void {
    // Unset null, undefined
    if (isUnset(value)) {
      return this.removeLocalStorage(key);
    }

    if (isServer || !this.options!.localStorage) {
      return;
    }

    const _key = this.options!.localStorage.prefix + key;

    try {
      localStorage.setItem(_key, encodeValue(value));
    } catch (e) {
      if (!this.options!.ignoreExceptions) {
        throw e;
      }
    }

    return value;
  }

  getLocalStorage(key: string): any {
    if (isServer || !this.options!.localStorage) {
      return;
    }

    const _key = this.options!.localStorage.prefix + key;

    const value = localStorage.getItem(_key);

    return decodeValue(value);
  }

  removeLocalStorage(key: string): void {
    if (isServer || !this.options!.localStorage) {
      return;
    }

    const _key = this.options!.localStorage.prefix + key;
    localStorage.removeItem(_key);
  }

  getLocalStorageKey(key: string): string {
    return this.options?.localStorage && this.options?.localStorage.prefix
      ? this.options!.localStorage.prefix + key
      : key;
  }

  // ------------------------------------
  // Session storage
  // ------------------------------------

  setSessionStorage<V>(key: string, value: V): V | void {
    // Unset null, undefined
    if (isUnset(value)) {
      return this.removeSessionStorage(key);
    }

    if (isServer || !this.options!.sessionStorage) {
      return;
    }

    const _key = this.options!.sessionStorage.prefix + key;

    try {
      sessionStorage.setItem(_key, encodeValue(value));
    } catch (e) {
      if (!this.options!.ignoreExceptions) {
        throw e;
      }
    }

    return value;
  }

  getSessionStorage(key: string): any {
    if (isServer || !this.options!.sessionStorage) {
      return;
    }

    const _key = this.options!.sessionStorage.prefix + key;

    const value = sessionStorage.getItem(_key);

    return decodeValue(value);
  }

  removeSessionStorage(key: string): void {
    if (isServer || !this.options!.sessionStorage) {
      return;
    }

    const _key = this.options!.sessionStorage.prefix + key;
    sessionStorage.removeItem(_key);
  }

  // ------------------------------------
  // Cookies
  // ------------------------------------
  getCookies(): Record<string, unknown> {
    //TODO server端实现
    const cookieStr = !isServer ? document.cookie : '';

    return parseCookies(cookieStr || '');
  }

  setCookie<V>(key: string, value: V, options: any = {}): V | void {
    //TODO server端实现
    if (!this.options!.cookie || isServer) {
      return;
    }

    const _prefix =
      options.prefix !== undefined
        ? options.prefix
        : this.options!.cookie.prefix;
    const _key = _prefix + key;
    const _options = Object.assign({}, this.options!.cookie.options, options);
    const _value = encodeValue(value);

    // Unset null, undefined
    if (isUnset(value)) {
      _options.maxAge = -1;
    }

    // Accept expires as a number for js-cookie compatiblity
    if (typeof _options.expires === 'number') {
      _options.expires = new Date(Date.now() + _options.expires * 864e5);
    }

    const serializedCookie = cookie.serialize(
      _key,
      _value,
      _options as CookieSerializeOptions,
    );

    // if (!isServer) {
    // Set in browser
    document.cookie = serializedCookie;
    // }
    //TODO Send Set-Cookie header from server side
    // else if (isServer) {
    // }

    return value;
  }

  getCookie(key: string): any {
    //TODO server端实现
    if (!this.options!.cookie || isServer) {
      return;
    }

    const _key = this.options!.cookie.prefix + key;

    const cookies = this.getCookies();
    const value = cookies[_key]
      ? decodeURIComponent(cookies[_key] as string)
      : undefined;

    return decodeValue(value);
  }

  removeCookie(
    key: string,
    options?: CookieSerializeOptions & { prefix?: string },
  ): void {
    this.setCookie(key, undefined, options);
  }

  // ------------------------------------
  // Common
  // ------------------------------------
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decodeValue(val: unknown): any {
    return decodeValue(val);
  }
}
